import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Ticket } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen grain bg-ink text-white flex items-center justify-center px-5">
      <div className="w-full max-w-lg text-center">
        {state === "checking" && (
          <div data-testid="payment-checking" className="flex flex-col items-center">
            <Loader2 className="animate-spin text-gold" size={40} />
            <p className="mt-6 text-white/60 uppercase tracking-[0.2em] text-sm">Confirming your payment…</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="payment-success">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto w-20 h-20 rounded-full border border-gold/50 flex items-center justify-center text-gold">
              <Check size={38} strokeWidth={1.5} />
            </motion.div>
            <h1 className="mt-8 font-serif font-light text-5xl tracking-tight">You're <span className="text-gold-gradient italic">in.</span></h1>
            {order && (
              <div className="mt-6 text-white/70 leading-relaxed">
                <p className="text-xl font-serif">{order.event_title}</p>
                <p className="text-white/50 text-sm mt-1">{order.event_date} · {order.event_venue}</p>
                <p className="mt-4">{order.quantity} × {order.tier_name} — <span className="text-gold">${Number(order.amount).toFixed(2)}</span></p>
              </div>
            )}
            <p className="mt-6 text-white/55">A confirmation with your tickets has been emailed to you.</p>
            <Link to="/" className="mt-9 inline-flex items-center gap-2 px-8 py-4 border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold hover:text-black transition-all duration-500" data-testid="back-home">
              Back to Home
            </Link>
          </motion.div>
        )}

        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-error">
            <Ticket className="mx-auto text-gold/60" size={40} />
            <h1 className="mt-6 font-serif font-light text-4xl">{state === "timeout" ? "Still processing…" : "Something went wrong"}</h1>
            <p className="mt-4 text-white/55">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual. If you completed checkout, your tickets will arrive by email shortly."
                : "We couldn't confirm this payment. If you were charged, please contact us and we'll sort it out."}
            </p>
            <Link to="/" className="mt-8 inline-flex items-center px-8 py-4 border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold hover:text-black transition-all duration-500">
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
